import React, { useEffect, useState, useMemo, useRef } from "react";
import "../../../../css/Common/Pages/RosemountInMediaPage/RosemountInMedia.css";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
// import BlogImage from "../../../../../assets/images/Blog.jpeg";
import ButtonGreen from "frontend/Components/DesignSystem/Buttons/ButtonGreen";
import { useNavigate } from "react-router-dom";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../../assets/icons/Events.svg";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import Testimonials from "../../../Components/Testimonials/Testimonials";
import VideoCarouselPopup from "../../../Components/DesignSystem/VideoCarousel";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "backend/firebaseConfig";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import {
  SEO_PAGES,
  SEO_SITE,
  getPageUrl,
  getLogoUrl,
} from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { getAdviserValues } from "frontend/Utils/GetAdviserValues";
import ArrowLeft from "frontend/Components/DesignSystem/Arrows/ArrowLeft";
import ArrowRight from "frontend/Components/DesignSystem/Arrows/ArrowRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * RosemountBlogPage component renders the blog page for Rosemount Financial advisers.
 *
 * - Displays a navigation bar with adviser-specific navigation items.
 * - Fetches and displays a list of blog posts from Firestore.
 * - Provides alternate text for blog images based on a predefined dictionary.
 * - Shows a hero animation banner for the blog section.
 * - Renders adviser values and a call-to-action to join as an adviser.
 * - Includes a section for adviser testimonials, events, and a contact form.
 * - Handles modal state for the "Hear from our Advisers" video carousel popup.
 *
 * @component
 * @returns {JSX.Element} The rendered RosemountBlogPage component.
 */
const PAGE_SIZE = 10;

const RosemountBlogPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];
  const topRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blogs, setBlogs] = useState([]);
  // const [page, setPage] = useState(1); // 1-based page index
  const [values, setValues] = useState([]);
  const WINDOW_SIZE = 3;
  const [page, setPage] = useState(1);
  const [windowStart, setWindowStart] = useState(1); // sticky window start
  // Call this whenever page changes (e.g., inside your next/prev handlers)
  function updateWindow(nextPage, total) {
    const maxStart = Math.max(1, total - WINDOW_SIZE + 1);

    // if nextPage before current window -> shift left
    if (nextPage < windowStart) {
      setWindowStart(Math.max(1, nextPage));
    }
    // if nextPage after current window -> shift right
    else if (nextPage > windowStart + WINDOW_SIZE - 1) {
      setWindowStart(Math.min(maxStart, nextPage - WINDOW_SIZE + 1));
    }
    // else do nothing (keep edges the same)
  }

  const goPrev = () => {
    if (page === 1) return;
    const next = page - 1;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const goNext = () => {
    if (page === totalPages) return;
    const next = page + 1;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const goTo = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    setPage(next);
    updateWindow(next, totalPages);
  };
  function getWindowPages(total, start, size = WINDOW_SIZE) {
    if (total <= 0) return [];
    const maxStart = Math.max(1, total - size + 1);
    const safeStart = Math.min(Math.max(1, start), maxStart);
    const end = Math.min(total, safeStart + size - 1);
    return Array.from({ length: end - safeStart + 1 }, (_, i) => safeStart + i);
  }

  const navigate = useNavigate();

  const handleToggleModal = () => setIsModalOpen((s) => !s);

  useEffect(() => {
    const fetchValues = async () => {
      const data = await getAdviserValues();
      setValues(data || []);
    };
    fetchValues();
  }, []);

  // Fetch whole array once (doc-as-array)
  const fetchBlogs = async () => {
    const docRef = doc(db, "Marketing", "Blogs");
    const snap = await getDoc(docRef);
    const items = snap.exists() ? snap.data().items || [] : [];
    // sort newest first using postedOn string → Date
    const toDate = (d) => (d ? new Date(d) : new Date(0));
    items.sort(
      (a, b) => toDate(b.postedOn).getTime() - toDate(a.postedOn).getTime()
    );
    setBlogs(items);
    setPage(1); // reset to first page when (re)loading
    setWindowStart(1); // reset window start`
    // requestAnimationFrame(() => {
    //   if (topRef.current) {
    //     const headerOffset = 88;
    //     const y =
    //       topRef.current.getBoundingClientRect().top +
    //       window.pageYOffset -
    //       headerOffset;
    //     window.scrollTo({ top: y, behavior: "smooth" });
    //   } else {
    //     window.scrollTo({ top: 0, behavior: "smooth" });
    //   }
    // });
  };

 

  useEffect(() => {
    fetchBlogs();
  }, []);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((blogs?.length || 0) / PAGE_SIZE)),
    [blogs]
  );

  // Current page slice
  const pageSlice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return blogs.slice(start, end);
  }, [blogs, page]);

  const altTextDictionary = {
    Cherry: "Cherry logo over two modern sky scrapers",
    "Financial%20Reporter":
      "Financial Reporter logo over two modern sky scrapers",
    "Health%20%26%20Protection":
      "Health & Protection logo over two modern sky scrapers",
    "IFA%20Magazine": "The IFA magazine logo on top of two modern sky scrapers",
    "Modern%20Lender": "Modern Lender logo over two modern sky scrapers",
    "Money%20Age": "Money Age logo over two modern sky scrapers",
    "Mortgage%20Finance%20Gazette":
      "Mortgage Finance Gazette logo over two modern sky scrapers",
    "Mortgage%20Introducer":
      "Mortgage Introducer logo over two modern sky scrapers",
    "Mortgage%20Solutions":
      "Mortgage Solutions logo over two modern sky scrapers",
    "Mortgage%20Soup": "Mortgage Soup logo over two modern sky scrapers",
    "Mortgage%20Strategy":
      "Mortgage Strategy logo over two modern sky scrapers",
    "Mortgage%20TV%20Introducer":
      "Mortgage TV Introducer logo over two modern sky scrapers",
    "Professional%20Adviser":
      "Professional Adviser logo over two modern sky scrapers",
    "Protection%20Reporter":
      "Protection Reporter logo over two modern sky scrapers",
    "The%20Intermediary": "The Intermediary logo over two modern sky scrapers",
  };

  /**
   * Returns an appropriate alt text for an image link based on a dictionary of keywords.
   *
   * @param {string} link - The URL or path of the image to analyze.
   * @returns {string} The alt text corresponding to a matched keyword in the dictionary,
   *                   or a default alt text if no match is found or if the link is falsy.
   *
   * The function checks if the provided link contains any of the keywords defined in
   * the `altTextDictionary` object. If a match is found, it returns the associated alt text.
   * If no match is found, it returns a default fallback alt text.
   */
  const getAltText = (link) => {
    if (!link) return "Image related to Rosemount Financial";
    for (const keyword in altTextDictionary) {
      if (link.includes(keyword)) return altTextDictionary[keyword];
    }
    return "Image from media coverage";
  };

   useEffect(() => {
     // clamp windowStart so it’s always valid
     const maxStart = Math.max(1, totalPages - WINDOW_SIZE + 1);
     setWindowStart((prev) => Math.min(prev, maxStart));

     // if current page is now out of range (e.g., fewer pages), clamp it
     setPage((prev) => Math.min(prev, totalPages));
   }, [totalPages]);
  useEffect(() => {
    // Simple: whole page top
    // window.scrollTo({ top: 0, behavior: "smooth" });

    // Better: scroll to your list’s top (avoids jumping above the navbar)
    if (topRef.current) {
      // If you have a sticky header, offset it (e.g., 88px)
      const headerOffset = 88; // adjust to your NavBar height
      const y =
        topRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [page]);

const pageUrl = getPageUrl(SEO_PAGES.rosemountBlog.path);
const logoUrl = getLogoUrl();

const blogStructuredData = {
  "@type": "Blog",
  name: "Rosemount Financial Solutions Blog",
  description: SEO_PAGES.rosemountBlog.description,
  url: pageUrl,
  publisher: {
    "@id": `${SEO_SITE.siteUrl}/#organization`,
  },
  blogPost: blogs.map((blog, index) => ({
    "@type": "BlogPosting",
    position: index + 1,
    headline: blog.title || "Rosemount Financial Solutions Blog Post",
    description:
      blog.content && blog.content.length > 160
        ? `${blog.content.substring(0, 157)}...`
        : blog.content || SEO_PAGES.rosemountBlog.description,
    image: blog.image || logoUrl,
    url: blog.link || pageUrl,
    datePublished: blog.postedOn || undefined,
    dateModified: blog.postedOn || undefined,
    author: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
    publisher: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": blog.link || pageUrl,
    },
  })),
};

  return (
    <div className="RosemountInMediaPage">
      <SeoHelmet
        page={SEO_PAGES.rosemountBlog}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Blog",
            url: getPageUrl("/RosemountBlog"),
          },
        ]}
        extraSchemas={[blogStructuredData]}
      />

      <NavBar navigationItems={navigationItems} />

      <div ref={topRef} />
      <HeroAnimation
        imgSrc={ImageDirectory["Blog"]}
        isAdviser={true}
        altText="Hands typing while looking through a website."
      >
        <h1>Blog</h1>
      </HeroAnimation>
      {/* Render 10 items per page */}
      {pageSlice.map((blog, i) => {
        const globalIndex = (page - 1) * PAGE_SIZE + i;
        return (
          <div
            key={globalIndex}
            className={`RosemountMedia ${
              globalIndex % 2 === 0 ? "even-background" : "odd-background"
            }`}
          >
            <div className="RosemountInMedia">
              <div className="RosemountInMedia-Content">
                {blog.title && <h2>{blog.title}</h2>}
                {/* {blog.postedOn && (
                  <div
                    className="Content-Small"
                    style={{ opacity: 0.7, marginBottom: 8 }}
                  >
                    {new Date(blog.postedOn).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                )} */}
                <div className="Content-Large item-content">{blog.content}</div>
                <ButtonGreen
                  buttonText="Read More"
                  onClick={() => window.open(blog.link, "_blank")}
                />
              </div>
              <img
                loading="eager"
                fetchPriority="high"
                src={blog.image}
                alt={getAltText(blog.image)}
              />
            </div>
          </div>
        );
      })}

      {/* Pagination controls */}
      {blogs.length > 0 && (
        <div className="PaginationControls">
          <button
            className="pagination-icon-btn"
            onClick={goPrev}
            disabled={page === 1}
          >
            <ChevronLeftIcon />
          </button>

          <div className="pagination-pages">
            {getWindowPages(totalPages, windowStart).map((p) => (
              <button
                key={p}
                className={`pagination-page-btn ${
                  p === page ? "is-active" : ""
                }`}
                onClick={() => goTo(p)}
                aria-current={p === page ? "page" : undefined}
              >
                <div className="Content-SemiBold">{p}</div>
              </button>
            ))}
          </div>

          <button
            className="pagination-icon-btn"
            onClick={goNext}
            disabled={page === totalPages}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}

      <AdviserValues
        values={values}
        isAdviser={true}
        title="Your Partner in Success"
      >
        <ButtonYellow
          buttonText="Join Us"
          onClick={() => navigate("/Adviser/JoinUs")}
        />
      </AdviserValues>

      <div className="About-Us">
        <div className="Adviser-Services-Holder1">
          <div onClick={handleToggleModal}>
            <CardProduct
              Icon={HearFromOurAdvisersIcon}
              title="Hear from our Advisers"
              isAdviser={true}
            />
          </div>
          <VideoCarouselPopup
            isOpen={isModalOpen}
            onClose={handleToggleModal}
          />
          <CardProduct
            Icon={EventsIcon}
            title="Events"
            isAdviser={true}
            link="/Adviser/Events"
          />
        </div>
      </div>

      <Testimonials isAdviser={true} />
      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default RosemountBlogPage;
